import type { Polygon } from 'geojson'

const EARTH_KM = 6371

const toRad = (deg: number) => (deg * Math.PI) / 180

export interface Point {
  lat: number
  lon: number
}

/** Great-circle distance in kilometres. */
export function distanceKm(a: Point, b: Point): number {
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_KM * Math.asin(Math.sqrt(h))
}

/**
 * A geodesic circle as a GeoJSON polygon. MapLibre's own circle layer sizes in
 * screen pixels, which would misrepresent a distance the moment you zoom.
 */
export function circlePolygon(centre: Point, km: number, steps = 96): Polygon {
  const ring: [number, number][] = []
  // Longitude degrees shrink with latitude; latitude degrees do not.
  const latDelta = km / 111.32
  const lonDelta = km / (111.32 * Math.cos(toRad(centre.lat)))

  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI
    ring.push([centre.lon + lonDelta * Math.cos(angle), centre.lat + latDelta * Math.sin(angle)])
  }

  return { type: 'Polygon', coordinates: [ring] }
}

export function formatKm(km: number): string {
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`
}
